// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { OrderNotesControl } from '@/order/components/order-notes-control';
import { Shipment } from '@/shipment/api/types';
import { updateShipment } from '@/shipment/api/update-shipment';
import { shipmentDetailQueryKey } from '@/shipment/hooks/use-shipment';
import { shipmentsQueryKey } from '@/shipment/hooks/use-shipments';
import { Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
  shipment: Pick<
    Shipment,
    'key' | 'shipmentNote' | 'deliveryNote' | 'receiptNote'
  >;
}

/**
 * Component that displays three separate editable note sections for a shipment:
 * - Shipment note
 * - Delivery note
 * - Receipt note
 *
 * Each section saves independently via the updateShipment API.
 */
export function ShipmentNotesControl(props: Props) {
  const queryClient = useQueryClient();
  const { shipment } = props;

  const handleSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: shipmentDetailQueryKey(shipment.key),
    });
    queryClient.invalidateQueries({ queryKey: shipmentsQueryKey });
  };

  return (
    <Stack spacing={3}>
      {/* Shipment note */}
      <Stack spacing={1}>
        <Typography variant="subtitle2" color="text.secondary">
          Shipment note
        </Typography>
        <OrderNotesControl
          orderNotes={shipment.shipmentNote}
          updateNotes={(shipmentNote) =>
            updateShipment(shipment.key, { shipmentNote })
          }
          onSuccess={handleSuccess}
        />
      </Stack>

      {/* Delivery note */}
      <Stack spacing={1}>
        <Typography variant="subtitle2" color="text.secondary">
          Delivery note
        </Typography>
        <OrderNotesControl
          orderNotes={shipment.deliveryNote}
          updateNotes={(deliveryNote) =>
            updateShipment(shipment.key, { deliveryNote })
          }
          onSuccess={handleSuccess}
        />
      </Stack>

      {/* Receive note */}
      <Stack spacing={1}>
        <Typography variant="subtitle2" color="text.secondary">
          Receive note
        </Typography>
        <OrderNotesControl
          orderNotes={shipment.receiptNote}
          updateNotes={(receiptNote) =>
            updateShipment(shipment.key, { receiptNote })
          }
          onSuccess={handleSuccess}
        />
      </Stack>
    </Stack>
  );
}
